import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Layouts
import DashboardLayout from './layouts/DashboardLayout';

// Pages
import Login from './pages/Login';
import Plans from './pages/Plans';
import AdminLogin from './pages/AdminLogin';
import AdminPanel from './pages/AdminPanel';

// Dashboard Pages
import Dashboard from './pages/dashboard/Dashboard';
import Websites from './pages/dashboard/Websites';
import Products from './pages/dashboard/Products';
import Orders from './pages/dashboard/Orders';
import Analytics from './pages/dashboard/Analytics';
import UserKit from './pages/dashboard/UserKit';
import Integrations from './pages/dashboard/Integrations';
import Workflows from './pages/dashboard/Workflows';
import Team from './pages/dashboard/Team';
import Settings from './pages/dashboard/Settings';
import Help from './pages/dashboard/Help';

// Wizard & Editor
import WebsiteWizard from './pages/WebsiteWizard';
import VisualEditor from './pages/VisualEditor';

// E-commerce
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Invoice from './pages/Invoice';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  if (!isAdmin) {
    return <Navigate to="/site/admin-login" replace />;
  }
  
  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/site/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/plans" element={<Plans />} />
      
      {/* Admin Routes */}
      <Route path="/site/admin-login" element={<AdminLogin />} />
      <Route path="/site/admin/*" element={
        <AdminRoute>
          <AdminPanel />
        </AdminRoute>
      } />
      
      {/* Dashboard Routes */}
      <Route path="/site" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="websites" element={<Websites />} />
        <Route path="products" element={<Products />} />
        <Route path="orders" element={<Orders />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="user-kit" element={<UserKit />} />
        <Route path="integrations" element={<Integrations />} />
        <Route path="workflows" element={<Workflows />} />
        <Route path="team" element={<Team />} />
        <Route path="settings" element={<Settings />} />
        <Route path="help" element={<Help />} />
      </Route>
      
      {/* Wizard & Editor */}
      <Route path="/site/wizard" element={<ProtectedRoute><WebsiteWizard /></ProtectedRoute>} />
      <Route path="/site/editor/:websiteId" element={<ProtectedRoute><VisualEditor /></ProtectedRoute>} />
      
      {/* E-commerce */}
      <Route path="/cart" element={<Cart />} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/invoice/:orderId" element={<Invoice />} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#22C55E',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#EF4444',
                secondary: '#fff',
              },
            },
          }}
        />
      </Router>
    </AuthProvider>
  );
}

export default App;
